import { yupResolver } from '@hookform/resolvers/yup';
import { Button, ButtonLevel, ButtonSize, Tooltip, TooltipTrigger } from '@vizit/atoms';
import { FormTextField } from '@vizit/forms';
import { FormDialog } from '@vizit/forms';
import { CheckIcon, UserPlus01Icon } from '@vizit/icons';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

type InviteUserFormSchema = {
  email: string;
};

interface InviteUserDialogProps {
  /**
   * The name of the organization to invite the user to
   */
  organizationName?: string;
  /**
   * Callback function to handle the score PDP api call
   */
  onSubmit: (data: InviteUserFormSchema, onSuccess: () => void, onError: () => void) => Promise<void>;
  /**
   * Extra fields rendered under the email input, for callers that need to collect more
   * than an address (e.g. role and scope). The caller owns their state and validation.
   */
  accessFields?: React.ReactNode;
  /**
   * Fired when the dialog resets, so the caller can clear the state behind accessFields.
   */
  onReset?: () => void;
}

/**
 * Dialog component for scoring Amazon Product Detail Pages (PDPs)
 * Allows users to enter an Amazon US product ASIN/URL to get a Vizit score
 *
 * @param props - The component props
 * @param props.onSubmit - Callback function when form is submitted
 *
 * @example
 * <InviteUserDialog onSubmit={(data) => console.log(data)} />
 */
function InviteUserDialog(props: InviteUserDialogProps) {
  const { organizationName, onSubmit, accessFields, onReset } = props;

  const [isOpen, setIsOpen] = React.useState(false);
  const [formButtonText, setFormButtonText] = React.useState<string | React.ReactNode>('Invite user');

  const formSchema = yup.object().shape({
    email: yup.string().email('Please enter a valid email.').required('Please enter an email.'),
  });

  const formMethods = useForm<InviteUserFormSchema>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      email: '',
    },
    mode: 'all',
    delayError: 650,
  });

  const { control, formState, handleSubmit, reset, setError } = formMethods;

  const handleOnOpenChange = React.useCallback((isOpen: boolean) => {
    setIsOpen(isOpen);
  }, []);

  const handleOnResetForm = React.useCallback(() => {
    reset({
      email: '',
    });

    setFormButtonText('Invite user');
    onReset?.();
  }, [reset, onReset]);

  const handleFormSubmit = React.useCallback(
    async (data: InviteUserFormSchema) => {
      const onSuccess = () => {
        setFormButtonText(<CheckIcon size={20} />);

        // Close the dialog after a delay.
        setTimeout(() => {
          handleOnOpenChange(false);
        }, 500);
      };

      const onError = () => {
        setError('email', {
          message: 'We are having trouble inviting this user, try again in a few minutes.',
          type: 'manual',
        });
      };

      await onSubmit(data, onSuccess, onError);
    },
    [onSubmit, setError, handleOnOpenChange],
  );

  return (
    <FormDialog
      formName="invite-user"
      formMethods={formMethods}
      isOpen={isOpen}
      onSubmit={handleSubmit(handleFormSubmit)}
      onResetForm={handleOnResetForm}
      onOpenChange={handleOnOpenChange}
    >
      <FormDialog.Trigger>
        <TooltipTrigger>
          <Button
            aria-label="Invite a user"
            className="text-sm font-semibold"
            size={ButtonSize.Lg}
            level={ButtonLevel.Secondary}
            iconStart={<UserPlus01Icon className="text-gray-utility-400 h-10 w-10" />}
          >
            <span className="max-sm:hidden">Invite</span>
          </Button>
          <Tooltip>Invite a user</Tooltip>
        </TooltipTrigger>
      </FormDialog.Trigger>
      <FormDialog.Title>Invite a user</FormDialog.Title>
      <FormDialog.Subtitle>
        {`Enter an email below to invite a user to ${organizationName ? `${organizationName}'s account` : 'your account'}.`}
      </FormDialog.Subtitle>
      <FormDialog.FormFields>
        <FormTextField
          isDisabled={formState.isSubmitting || formState.isSubmitSuccessful}
          control={control}
          name="email"
          label="Email"
          type="email"
          inputProps={{
            'data-1p-ignore': 'true',
            'data-lpignore': 'true',
          }}
          isRequired
        />
        {accessFields}
      </FormDialog.FormFields>
      <FormDialog.SubmitButton>{formButtonText}</FormDialog.SubmitButton>
    </FormDialog>
  );
}

export { InviteUserDialog };
export type { InviteUserFormSchema, InviteUserDialogProps };
